import { HttpErrorResponse, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, catchError, switchMap, throwError } from 'rxjs';

import { API_CONFIG, ApiConfig } from '../config/api.config';
import { AuthService } from '../../features/auth/services/auth.service';
import { AuthSessionStorageService } from '../../features/auth/services/auth-session-storage.service';
import { NotificationService } from '../../shared/services/notification.service';

export const authErrorInterceptor: HttpInterceptorFn = (request, next) => {
  const apiConfig = inject(API_CONFIG);
  const authService = inject(AuthService);
  const authSessionStorageService = inject(AuthSessionStorageService);
  const notificationService = inject(NotificationService);
  const router = inject(Router);

  return next(request).pipe(
    catchError((error: unknown) => {
      if (!(error instanceof HttpErrorResponse)) {
        return throwError(() => error);
      }

      if (!isApiRequest(request.url, apiConfig.baseUrl)) {
        return throwError(() => error);
      }

      // Never try to refresh on the auth endpoints themselves (prevents loops).
      if (error.status !== 401 || isAuthBypassRequest(request.url, apiConfig.auth.refresh.endpoint)) {
        return throwError(() => error);
      }

      // Only Cognito sessions can be refreshed; a legacy LOCAL JWT has no refresh
      // cookie, so it goes straight to a forced logout.
      if (!apiConfig.auth.refresh.enabled || !authService.canRefreshSession()) {
        return forceLogout(error, authService, notificationService, router, apiConfig);
      }

      // Single refresh shared across concurrent 401s; on success retry the original
      // request exactly once with the new token. Errors from the retried request are
      // NOT caught here (this catchError only wraps the first attempt), so there is
      // no second refresh and no loop.
      return authService.refreshSession().pipe(
        switchMap((refreshed) => {
          if (!refreshed) {
            return forceLogout(error, authService, notificationService, router, apiConfig);
          }

          return next(withFreshToken(request, authService, authSessionStorageService, apiConfig));
        })
      );
    })
  );
};

function forceLogout(
  error: HttpErrorResponse,
  authService: AuthService,
  notificationService: NotificationService,
  router: Router,
  apiConfig: ApiConfig
): Observable<never> {
  authService.clearLocalSession();
  notificationService.warning('Your session expired. Please sign in again.', 'Session Expired');

  const redirectTo = router.url.startsWith('/auth') ? '/dashboard' : router.url;
  void router.navigate([apiConfig.auth.unauthorizedRedirectPath], {
    queryParams: { redirectTo }
  });

  return throwError(() => error);
}

function withFreshToken(
  request: HttpRequest<unknown>,
  authService: AuthService,
  authSessionStorageService: AuthSessionStorageService,
  apiConfig: ApiConfig
): HttpRequest<unknown> {
  // This interceptor runs after authInterceptor, so the retried request bypasses
  // it — attach the freshly refreshed token here.
  const token = authService.getAccessToken() ?? authSessionStorageService.loadAccessToken();

  if (!token) {
    return request;
  }

  const prefix = apiConfig.auth.tokenPrefix;
  const normalizedToken = token.trim().replace(new RegExp(`^${escapeRegExp(prefix)}\\s+`, 'i'), '');

  return request.clone({
    setHeaders: {
      [apiConfig.auth.headerName]: `${prefix} ${normalizedToken}`
    }
  });
}

function isAuthBypassRequest(url: string, refreshEndpoint: string): boolean {
  return (
    url.includes('/auth/login') ||
    url.includes('/auth/complete-new-password') ||
    url.includes('/auth/forgot-password') ||
    url.includes('/auth/confirm-forgot-password') ||
    url.includes('/auth/logout') ||
    url.includes(refreshEndpoint)
  );
}

function isApiRequest(url: string, baseUrl: string): boolean {
  const normalizedBaseUrl = baseUrl.replace(/\/$/, '');
  return url.startsWith(normalizedBaseUrl);
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}
